import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useAddFranchisesToAccount,
  useRemoveFranchisesFromAccount
} from "./ddc-pages-franchise-management-fetch";

export interface FranchiseChanges {
  toAdd: string[];
  toRemove: string[];
  hasChanges: boolean;
}

export interface UseFranchiseChangesOptions {
  accountId: string;
  initialFranchises?: string[];
  onSuccess?: (message: string) => void;
  onError?: (error: string) => void;
  onPartialFailure?: (
    addFailures: { franchiseName: string; reason: string }[],
    removeFailures: { franchiseName: string; reason: string }[]
  ) => void;
}

export function useFranchiseChanges({
  accountId,
  initialFranchises = [],
  onSuccess,
  onError,
  onPartialFailure
}: UseFranchiseChangesOptions) {
  const [originalFranchises, setOriginalFranchises] =
    useState<string[]>(initialFranchises);
  const [selectedFranchises, setSelectedFranchises] =
    useState<string[]>(initialFranchises);
  const [changes, setChanges] = useState<FranchiseChanges>({
    toAdd: [],
    toRemove: [],
    hasChanges: false
  });

  const changesRef = useRef(changes);
  const selectedFranchisesRef = useRef(selectedFranchises);

  useEffect(() => {
    changesRef.current = changes;
  }, [changes]);

  useEffect(() => {
    selectedFranchisesRef.current = selectedFranchises;
  }, [selectedFranchises]);

  const [isSaving, setIsSaving] = useState(false);

  const queryClient = useQueryClient();
  const { mutate: addFranchises } = useAddFranchisesToAccount(
    undefined,
    undefined,
    true
  );
  const { mutate: removeFranchises } = useRemoveFranchisesFromAccount(
    undefined,
    undefined,
    true
  );

  useEffect(() => {
    if (initialFranchises.length > 0 && originalFranchises.length === 0) {
      setOriginalFranchises(initialFranchises);
      setSelectedFranchises(initialFranchises);
    }
  }, [initialFranchises, originalFranchises.length]);

  const initializeFranchises = useCallback((franchiseNames: string[]) => {
    setOriginalFranchises(prev => {
      if (
        prev.length === franchiseNames.length &&
        prev.every((name, index) => name === franchiseNames[index])
      ) {
        return prev;
      }
      return franchiseNames;
    });
    setSelectedFranchises(prev => {
      if (
        prev.length === franchiseNames.length &&
        prev.every((name, index) => name === franchiseNames[index])
      ) {
        return prev;
      }
      return franchiseNames;
    });
  }, []);

  const updateSelectedFranchises = (franchiseNames: string[]) => {
    setSelectedFranchises(franchiseNames);
  };

  useEffect(() => {
    if (originalFranchises === selectedFranchises) {
      if (changes.hasChanges) {
        setChanges({ toAdd: [], toRemove: [], hasChanges: false });
      }
      return;
    }

    const originalSet = new Set(originalFranchises);
    const selectedSet = new Set(selectedFranchises);

    const toAdd = selectedFranchises.filter(f => !originalSet.has(f));
    const toRemove = originalFranchises.filter(f => !selectedSet.has(f));

    const hasChanges = toAdd.length > 0 || toRemove.length > 0;
    setChanges({ toAdd, toRemove, hasChanges });
  }, [selectedFranchises, originalFranchises, changes.hasChanges]);

  const saveChanges = useCallback(async () => {
    const currentChanges = changesRef.current;
    const currentSelectedFranchises = selectedFranchisesRef.current;

    if (!currentChanges.hasChanges) {
      return;
    }

    setIsSaving(true);
    try {
      const promises: Promise<any>[] = [];
      let addResult: any = null;
      let removeResult: any = null;

      if (currentChanges.toAdd.length > 0) {
        promises.push(
          new Promise<void>((resolve, reject) => {
            addFranchises(
              { accountId, franchiseNames: currentChanges.toAdd },
              {
                onSuccess: data => {
                  addResult = data;
                  resolve();
                },
                onError: error => reject(error)
              }
            );
          })
        );
      }

      if (currentChanges.toRemove.length > 0) {
        promises.push(
          new Promise<void>((resolve, reject) => {
            removeFranchises(
              { accountId, franchiseNames: currentChanges.toRemove },
              {
                onSuccess: data => {
                  removeResult = data;
                  resolve();
                },
                onError: error => reject(error)
              }
            );
          })
        );
      }

      // Execute all operations in parallel
      await Promise.all(promises);

      // Invalidate queries once after all operations complete
      queryClient.invalidateQueries({
        queryKey: ["ddc-pages-franchise-management", "account", accountId]
      });

      setOriginalFranchises([...currentSelectedFranchises]);

      // Consolidate failed franchises from both add and remove operations
      const addFailures: { franchiseName: string; reason: string }[] = [];
      const removeFailures: { franchiseName: string; reason: string }[] = [];
      if (
        addResult &&
        addResult.failedFranchiseNames &&
        addResult.failedFranchiseNames.length > 0
      ) {
        addFailures.push(...addResult.failedFranchiseNames);
      }

      if (
        removeResult &&
        removeResult.failedFranchiseNames &&
        removeResult.failedFranchiseNames.length > 0
      ) {
        removeFailures.push(...removeResult.failedFranchiseNames);
      }

      if (addFailures.length > 0 || removeFailures.length > 0) {
        if (onPartialFailure) {
          onPartialFailure(addFailures, removeFailures);
        }
      } else if (onSuccess) {
        onSuccess("Account changes saved successfully");
      }
    } catch (error) {
      if (onError) {
        onError("Error saving account changes");
      }
    } finally {
      setIsSaving(false);
    }
  }, [
    accountId,
    addFranchises,
    removeFranchises,
    queryClient,
    onSuccess,
    onError,
    onPartialFailure
  ]);

  return {
    originalFranchises,
    selectedFranchises,
    changes,
    initializeFranchises,
    updateSelectedFranchises,
    saveChanges,
    isSaving,
    canSave: changes.hasChanges && !isSaving
  };
}

export function calculateFranchiseChanges(
  original: string[],
  selected: string[]
): FranchiseChanges {
  const originalSet = new Set(original);
  const selectedSet = new Set(selected);

  const toAdd = selected.filter(f => !originalSet.has(f));
  const toRemove = original.filter(f => !selectedSet.has(f));
  const hasChanges = toAdd.length > 0 || toRemove.length > 0;

  return { toAdd, toRemove, hasChanges };
}
